import { Button } from '@unifyapps/ui/components/Button';
import { Divider } from '@unifyapps/ui/components/Divider';
import type { Markdown as BaseMarkdown } from '@unifyapps/ui/components/Markdown';
import { TableCell } from '@unifyapps/ui/components/Table';
import { Typography, type TypographyProps } from '@unifyapps/ui/components/Typography';
import React, { lazy, type ReactNode } from 'react';
import { TypographyWithTooltip } from '../Typography/TypographyWithTooltip';
import { TableHeaderCell } from './components/TableHeaderCell';
import { TableRenderer } from './components/TableRenderer';
import type { MarkdownTableColumnSize } from './utils/tableColumnSizingUtil';

export const TABLE_CELL_CLASSNAME = 'markdown-table-cell';
/*
 * break-anywhere is used to break the code block at the end of the line
 * bg-[var(--palette-gray-100)] is the background color of the block
 * text-[var(--palette-gray-900)] is the color text in the block
 * px-sm is the horizontal padding
 * rounded-xs is the border radius
 * font-normal is the font weight
 * text-[length:var(--inline-code-font-size,12px)] is the font size
 * tracking-[-0.02em] is the letter-spacing
 * [&::before]:hidden [&::after]:hidden is used to hide the ` sign before and after the text`
 * design link: https://www.figma.com/design/MEUM4gvEDCStVYPzPOtcit/Base-ES?node-id=6421-139416&m=dev
 */
const CODE_CLASSNAME =
  'break-anywhere bg-[var(--palette-gray-100)] text-[var(--palette-gray-900)] px-sm rounded-xs font-normal text-[length:var(--inline-code-font-size,12px)] tracking-[-0.02em] [&::before]:hidden [&::after]:hidden';

interface MarkdownTableCellRendererProps {
  children?: ReactNode;
  columnSize?: MarkdownTableColumnSize;
}

function TableDataCell({ children, columnSize }: MarkdownTableCellRendererProps) {
  return (
    <TableCell className={TABLE_CELL_CLASSNAME} data-col-size={columnSize}>
      <TypographyWithTooltip maxNumberOfLines={2}>{children || '-'}</TypographyWithTooltip>
    </TableCell>
  );
}

const renderers = {
  table: TableRenderer,
  td: TableDataCell,
  th: ({ children, columnSize }: MarkdownTableCellRendererProps) => (
    <TableHeaderCell className={TABLE_CELL_CLASSNAME} columnSize={columnSize}>
      {children}
    </TableHeaderCell>
  ),
};

const importCodeHighLighter = () =>
  import(
    /* webpackChunkName: "CodeHighlighter" */
    './components/CodeHighlighter'
  );

const CodeHighlighter = lazy(importCodeHighLighter);

export type CommonMarkdownComponentsProps = {
  codeHighlighterMode?: 'dark' | 'light';
  inlineCodeClassName?: string;
};

export function getCommonMarkdownComponents({
  codeHighlighterMode = 'light',
  inlineCodeClassName = CODE_CLASSNAME,
}: CommonMarkdownComponentsProps = {}): React.ComponentProps<typeof BaseMarkdown>['components'] {
  return {
    code(props) {
      const { children, className = '', node } = props;
      const [, language] = className.split('-');
      const isInline = node?.position?.start.line === node?.position?.end.line;
      if (isInline) {
        return <code className={inlineCodeClassName}>{children}</code>;
      }

      return (
        <React.Suspense fallback={null}>
          <CodeHighlighter
            language={language}
            mode={codeHighlighterMode}
            value={children as string}
          />
        </React.Suspense>
      );
    },

    pre(props) {
      if (props.node?.tagName === 'pre') {
        return props.children as string;
      }
      return <pre>{props.children}</pre>;
    },

    button(props) {
      const { className, children } = props;
      return (
        <Button className={`${className} no-prose`} size="xs" variant="ghost">
          {children}
        </Button>
      );
    },
    hr(props) {
      return <Divider className={props.className} />;
    },

    a(props) {
      const { href } = props;
      return (
        <a
          className="text-brand-tertiary no-underline hover:underline hover:text-brand-secondary active:text-brand-secondary"
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {props.children}
        </a>
      );
    },
  };
}

/** default instance for backward compatibility when no codeHighlighterMode is needed */
export const CommonMarkdownComponents: React.ComponentProps<typeof BaseMarkdown>['components'] =
  getCommonMarkdownComponents({});

export const MarkdownComponents: React.ComponentProps<typeof BaseMarkdown>['components'] = {
  ...CommonMarkdownComponents,
  ...renderers,
  //todo: this is the good default, but we need to add a way to override it
  li({
    children,
    index,
    ordered,
    color,
  }: {
    children: React.ReactNode;
    index?: number;
    ordered?: boolean;
    color?: TypographyProps['color'];
  }) {
    return (
      <li
        className="m-none p-none leading-[var(--lineHeight-text-md)]"
        // value prop sets the number displayed for ordered list items (ol)
        value={ordered && index !== undefined ? index + 1 : undefined}
      >
        <Typography color={color ?? 'text-primary'}>{children}</Typography>
      </li>
    );
  },
  ol({ children, start = 1 }: { children: React.ReactNode; start?: number }) {
    // start prop specifies the starting number for an ordered list
    return (
      <ol className="marker:text-inherit !mt-0" start={start}>
        {children}
      </ol>
    );
  },
  ul: ({ children }: { children: ReactNode }) => (
    <ul className="marker:text-inherit !mt-0">{children}</ul>
  ),
};
